import { NextSeo } from "next-seo";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { Routes } from "~/routes";
import { Navbar } from "~/components/Navbar";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { PricingPage } from "~/modules/billing/pages/pricing";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { PageWrapper, SidebarLayout } from "~/components/SidebarLayout";
import { useUser } from "~/modules/user/hooks/useUser";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { queryMediaCitations } from "~/modules/media_citations/api/router";
import { MediaCitation } from "~/modules/media_citations/types";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";

// // // //

const SEO_TITLE = "Pricing";
const SEO_DESCRIPTION =
    "View RWA.xyz pricing plans for data access, API usage, and enterprise analytics on tokenized real-world assets.";

// // // //

export async function getStaticProps() {
    let citations: MediaCitation[] = [];

    try {
        const allCitations = await queryMediaCitations();
        // Take the 20 latest citations for the carousel
        citations = allCitations.slice(0, 20);
    } catch (error) {
        console.error("Failed to fetch citations:", error);
    }

    return {
        props: {
            citations,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
}

// // // //

export default function Pricing(props: { citations: MediaCitation[] }) {
    const { citations } = props;
    const { isLoggedIn } = useUser();
    return (
        <PageTrack route={Routes.pricing}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.pricing)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.pricing),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "RWA.xyz pricing, data plans, API pricing, tokenization data, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.pricing}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.pricing,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => (
                    <>
                        <Navbar route={Routes.pricing} user={user} />
                        <SidebarLayout pathname={Routes.pricing}>
                            <PageWrapper className="max-w-7xl">
                                <PricingPage
                                    user={user}
                                    isLoggedIn={isLoggedIn}
                                    citations={citations}
                                />
                            </PageWrapper>
                        </SidebarLayout>
                    </>
                )}
            </UserFetcher>
        </PageTrack>
    );
}
